<template>
  <modal-frame v-slot="{ close }" size="sm" :esc-close="!userStore.user?.is_password_reset_required">
    <div class="modal modal-update-password" role="dialog" aria-modal="true">
      <modal-header
        :title="$t('user.password-change-modal.title')"
        :show-close="!userStore.user?.is_password_reset_required"
        class="modal-update-password__header" />

      <modal-content class="modal-update-password__content">
        <form name="password-change" class="modal-update-password__form">
          <InputText :value="userStore.user?.email" hidden autocomplete="email" />

          <input-password-label
            v-model="currentPassword"
            :disabled="isUpdatePasswordLoading"
            :label="$t('user.password-change-modal.form.current-password-label')"
            :input-props="{ autocomplete: 'current-password' }"
            :feedback="false"
            input-class="w-full"
            input-id="current-password" />

          <input-password-label
            v-model="newPassword"
            :disabled="isUpdatePasswordLoading"
            :label="$t('user.password-change-modal.form.new-password-label')"
            :input-props="{ autocomplete: 'new-password' }"
            input-class="w-full"
            input-id="new-password" />

          <input-password-label
            v-model="confirmNewPassword"
            :disabled="isUpdatePasswordLoading"
            :label="$t('user.password-change-modal.form.confirm-new-password-label')"
            :input-props="{ autocomplete: 'new-password' }"
            :feedback="false"
            input-class="w-full"
            input-id="confirm-new-password" />
        </form>
      </modal-content>

      <modal-footer class="modal-update-password__footer">
        <p v-if="userStore.user?.is_password_reset_required" class="text-center">
          {{ $t('user.password-change-modal.force-change-info') }}
        </p>

        <Button
          :loading="isUpdatePasswordLoading"
          :disabled="isConfirmButtonDisabled"
          :label="$t('user.password-change-modal.confirm-button')"
          size="large"
          rounded
          @click="onUpdatePasswordClicked" />

        <LazyButton
          v-if="!userStore.user?.is_password_reset_required"
          :disabled="isUpdatePasswordLoading"
          :label="$t('user.password-change-modal.cancel-button')"
          size="large"
          rounded
          outlined
          @click="close(false)" />
      </modal-footer>
    </div>
  </modal-frame>
</template>

<script setup lang="ts">
import { ApiError } from '~/api/generated';

const { t } = useI18n();

const { success } = useCustomToasts();

const { extractApiError } = useApiUtils();

const userStore = useUserStore();

const $modal = inject('$modal', {
  close: (success: boolean) => success
});

const currentPassword = ref<string>();

const newPassword = ref<string>();

const confirmNewPassword = ref<string>();

const isConfirmButtonDisabled = computed(
  () =>
    !currentPassword.value ||
    !newPassword.value ||
    newPassword.value !== confirmNewPassword.value ||
    isUpdatePasswordLoading.value
);

const { execute: updatePassword, isLoading: isUpdatePasswordLoading } = useAsyncState(
  userStore.updatePassword,
  undefined,
  {
    immediate: false,
    onSuccess: () => {
      $modal.close(true);

      // Wait for layout to change before showing success message
      // TODO: Should be replaced with a proper solution
      setTimeout(() => {
        success(t('user.notifications.password-changed'));
      }, 300);
    },
    onError: (apiError) => {
      extractApiError(apiError as ApiError, 'user.notifications.password-change-failed');
    }
  }
);

const onUpdatePasswordClicked = async () => {
  await updatePassword(0, {
    requestBody: {
      old_password: currentPassword.value!,
      new_password: newPassword.value!
    }
  });
};
</script>

<style lang="postcss">
.modal-update-password {
  &__header {
    h2 {
      @apply text-xl;
    }
  }

  &__content {
    @apply h-auto;
  }

  &__form,
  &__footer {
    @apply flex flex-col gap-5;
  }
}
</style>
